/** @jsxImportSource @emotion/react */
import { TextFields } from '@zip/business-components';
import { State } from 'enums';
import { useEffect, useRef, useState } from 'react';
import { AddressErrors, InlineAddress } from 'types';
import { renderFieldError } from 'utils/validators';
import * as styles from './AddressLookupInline.styles';

const stateOptions = Object.values(State).map((state) => ({
  value: state,
  label: state,
}));

const EMPTY_ADDRESS: Required<Omit<InlineAddress, 'country'>> = {
  unitNumber: '',
  streetNumber: '',
  streetName: '',
  suburb: '',
  postcode: '',
  state: '',
};

const ALL_TOUCHED: Record<keyof Omit<InlineAddress, 'country'>, boolean> = {
  unitNumber: true,
  streetNumber: true,
  streetName: true,
  suburb: true,
  postcode: true,
  state: true,
};

const buildInitialManualValues = (address?: InlineAddress): InlineAddress => ({
  ...EMPTY_ADDRESS,
  ...(address ?? {}),
});

const validateManualAddress = (values: InlineAddress): AddressErrors => {
  const errors: AddressErrors = {};

  if (!values.streetName) {
    errors.streetName = 'Please enter a street name';
  }

  if (!values.streetNumber) {
    errors.streetNumber = 'Please enter a street number';
  }

  if (!values.suburb) {
    errors.suburb = 'Please enter a suburb';
  }

  if (!values.postcode) {
    errors.postcode = 'Please enter a postcode';
  } else if (!/^[0-9]{4}$/i.test(values.postcode)) {
    errors.postcode = 'Please enter a valid postcode';
  }

  if (!values.state) {
    errors.state = 'Please select a state';
  }

  return errors;
};

interface ManualAddressFieldsProps {
  address?: InlineAddress;
  onChange: (values: InlineAddress) => void;
  addressLookupButton: JSX.Element;
  showValidationErrors?: boolean;
}

const ManualAddressFields = ({
  address,
  onChange,
  showValidationErrors = false,
  addressLookupButton,
}: ManualAddressFieldsProps): JSX.Element => {
  const [values, setValues] = useState<InlineAddress>(() =>
    buildInitialManualValues(address)
  );
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const errors = validateManualAddress(values);

  const isMountedRef = useRef(false);

  // Mark every field as touched the moment the parent flips `showValidationErrors` on, so per-field errors appear immediately.
  useEffect(() => {
    if (!showValidationErrors) {
      return;
    }

    setTouched(ALL_TOUCHED);
  }, [showValidationErrors]);

  // Push value updates back to the parent
  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }

    onChange(values);
  }, [values, onChange]);

  const setFieldValue = (field: keyof InlineAddress, value: string): void => {
    setValues((current) => ({ ...current, [field]: value }));
  };

  const setFieldTouched = (field: keyof InlineAddress): void => {
    setTouched((current) => ({ ...current, [field]: true }));
  };

  return (
    <div css={styles.manualAddressRoot}>
      <div css={styles.manualAddressFields}>
        <TextFields.Outlined
          type="text"
          name="streetName"
          label="Street name *"
          css={[styles.textfield, styles.fullWidthField]}
          onChange={(event): void => {
            setFieldValue('streetName', event.target.value);
          }}
          onBlur={(): void => {
            setFieldTouched('streetName');
          }}
          value={values.streetName ?? ''}
          error={Boolean(touched.streetName && errors.streetName)}
          helperText={renderFieldError(touched.streetName, errors.streetName)}
          slotProps={{ formHelperText: { className: 'body4' } }}
        />

        <TextFields.Outlined
          type="text"
          name="streetNumber"
          label="Street number *"
          css={[styles.textfield, styles.fullWidthField]}
          onChange={(event): void => {
            setFieldValue('streetNumber', event.target.value);
          }}
          onBlur={(): void => {
            setFieldTouched('streetNumber');
          }}
          value={values.streetNumber ?? ''}
          error={Boolean(touched.streetNumber && errors.streetNumber)}
          helperText={renderFieldError(
            touched.streetNumber,
            errors.streetNumber
          )}
          slotProps={{ formHelperText: { className: 'body4' } }}
        />

        <TextFields.Outlined
          type="text"
          name="unitNumber"
          label="Unit or apartment number"
          css={[styles.textfield, styles.fullWidthField]}
          onChange={(event): void => {
            setFieldValue('unitNumber', event.target.value);
          }}
          onBlur={(): void => setFieldTouched('unitNumber')}
          value={values.unitNumber ?? ''}
          error={Boolean(touched.unitNumber && errors.unitNumber)}
          helperText={renderFieldError(touched.unitNumber, errors.unitNumber)}
          slotProps={{ formHelperText: { className: 'body4' } }}
        />

        <TextFields.Outlined
          type="text"
          name="suburb"
          label="Suburb *"
          css={[styles.textfield, styles.fullWidthField]}
          onChange={(event): void => {
            setFieldValue('suburb', event.target.value);
          }}
          onBlur={(): void => {
            setFieldTouched('suburb');
          }}
          value={values.suburb ?? ''}
          error={Boolean(touched.suburb && errors.suburb)}
          helperText={renderFieldError(touched.suburb, errors.suburb)}
          slotProps={{ formHelperText: { className: 'body4' } }}
        />

        <TextFields.Outlined
          type="text"
          name="postcode"
          label="Postcode *"
          css={styles.textfield}
          onChange={(event): void =>
            setFieldValue('postcode', event.target.value)
          }
          onBlur={(): void => {
            setFieldTouched('postcode');
          }}
          value={values.postcode ?? ''}
          error={Boolean(touched.postcode && errors.postcode)}
          helperText={renderFieldError(touched.postcode, errors.postcode)}
          slotProps={{ formHelperText: { className: 'body4' } }}
        />

        <TextFields.Select
          name="state"
          label="State *"
          css={styles.textfield}
          options={stateOptions}
          slotProps={{
            select: {
              onChange: (event): void => {
                setFieldValue('state', event.target.value as string);
              },
              onBlur: (): void => {
                setFieldTouched('state');
              },
              value: values.state ?? '',
            },
            formHelperText: { className: 'body4' },
          }}
          error={Boolean(touched.state && errors.state)}
          helperText={renderFieldError(touched.state, errors.state)}
        />
      </div>

      {addressLookupButton}
    </div>
  );
};

export default ManualAddressFields;
